import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { CalendarOff, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import GoogleCalendarIcon from "@/components/Agenda/GoogleCalendarIcon";
import {
  DIAS_CURTOS,
  DIAS_LONGOS,
  STATUS_REUNIAO,
  ehHoje,
  hhmm,
} from "@/components/Agenda/agenda-utils";
import {
  MINUTOS_NO_DIA,
  complemento,
  empacotar,
  faixaDeHoras,
  hhmmParaMinutos,
  recorteNoDia,
  type Intervalo,
} from "@/components/Agenda/grade-utils";

/**
 * Altura de uma hora, por densidade. No "normal" (64px) a reunião de 30 min
 * tem 32px — cabe hora e título numa linha; "compacto" mostra o dia inteiro
 * sem rolar em telas menores; "amplo" dá espaço para contato e participantes.
 */
export const DENSIDADES = { compacto: 40, normal: 64, amplo: 96 } as const;
export type Densidade = keyof typeof DENSIDADES;

export type Reuniao = {
  meeting: {
    id: number;
    title: string;
    startAt: Date | string;
    endAt: Date | string;
    status: string;
    locationType?: string;
    locationText?: string | null;
    meetingUrl?: string | null;
  };
  contact?: { name?: string | null } | null;
  attendees?: Array<{ user?: { name?: string | null; email?: string | null } | null }>;
};

export type Bloqueio = {
  block: {
    id: number;
    userId: number;
    startAt: Date | string;
    endAt: Date | string;
    reason: string | null;
    allDay: boolean;
    sourceMeetingId: number | null;
    externalProvider?: string | null;
    /** false = "Disponível" no Google: mostra, não ocupa. */
    busy?: boolean;
    externalUrl?: string | null;
  };
  user?: { name?: string | null; email?: string | null } | null;
};

type Faixa = { dayOfWeek: number; startTime: string; endTime: string };

type Props = {
  /** 7 dias (semana) ou 1 (dia). A grade se adapta ao que vier. */
  dias: Date[];
  densidade?: Densidade;
  reunioes: Reuniao[];
  bloqueios: Bloqueio[];
  /**
   * Semana típica de quem está sendo exibido. Quando é a equipe toda, a
   * união das faixas de todos — a grade sombreia só o que está fora do
   * horário de TODO MUNDO.
   */
  faixas: Faixa[];
  /** Mostrando a agenda de várias pessoas: os cards precisam dizer de quem é. */
  modoEquipe: boolean;
  /** Quem está olhando — para recusar o clique no próprio bloqueio mesmo no modo equipe. */
  meuId?: number;
  podeMarcar: boolean;
  onAbrirReuniao: (id: number) => void;
  onMarcar: (quando: Date) => void;
  /**
   * Bloquear a partir de um horário vazio. Quando existe, o clique no fundo
   * abre um menu "reunião ou bloqueio" em vez de ir direto para a reunião.
   */
  onBloquear?: (inicio: Date, fim: Date) => void;
  /** Clique num bloqueio (manual): abrir para editar. */
  onAbrirBloqueio?: (b: Bloqueio) => void;
  /** Clique no número do dia (só na semana): abre aquele dia sozinho. */
  onEscolherDia?: (dia: Date) => void;
};

/**
 * Bloqueio e compromisso do Google são cartões de cor cheia com barra à
 * esquerda, como no Google Agenda — vinho suave para "bloqueei" (firme o
 * bastante para dizer "aqui não", sem a hachura que cansava), âmbar para
 * "está no meu Google". A hachura diagonal de antes chamava mais atenção que as
 * reuniões, que são o que a grade existe para mostrar.
 */
const CARTAO_BLOQUEIO = "border-l-[3px] border-l-rose-700 bg-rose-200/60 dark:bg-rose-950/60 text-rose-950 dark:text-rose-100";
const CARTAO_GOOGLE = "border-l-[3px] border-l-amber-400 bg-amber-100/90 dark:bg-amber-950/50 text-amber-900 dark:text-amber-100";
const CARTAO_GOOGLE_LIVRE = "border-l-[3px] border-l-amber-300 bg-amber-50/80 dark:bg-amber-950/25 text-amber-800 dark:text-amber-200";

/**
 * Semana em grade horária: horas nas linhas, dias nas colunas.
 *
 * A pergunta que a grade responde de longe é "essa pessoa está livre aí?".
 * Por isso três camadas, de baixo para cima: fora do horário de trabalho em
 * cinza, bloqueio em hachura vermelha com o motivo, reunião por cima. Dia
 * inteiro bloqueado (férias) sai da grade e vira um selo no cabeçalho, senão
 * a coluna vermelha inteira escondia o resto.
 */
export default function WeekGrid({
  dias,
  reunioes,
  bloqueios,
  faixas,
  densidade = "normal",
  modoEquipe,
  meuId,
  podeMarcar,
  onAbrirReuniao,
  onMarcar,
  onBloquear,
  onAbrirBloqueio,
  onEscolherDia,
}: Props) {
  // Menu "reunião ou bloqueio" ancorado no ponto do clique.
  const [menu, setMenu] = useState<{ x: number; y: number; quando: Date; motivoSemReuniao?: string } | null>(null);
  useEffect(() => {
    if (!menu) return;
    const fechar = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      setMenu(null);
    };
    window.addEventListener("keydown", fechar);
    window.addEventListener("scroll", fechar, true);
    return () => {
      window.removeEventListener("keydown", fechar);
      window.removeEventListener("scroll", fechar, true);
    };
  }, [menu]);
  const PX_POR_HORA = DENSIDADES[densidade];
  const PX_POR_MINUTO = PX_POR_HORA / 60;
  const umDia = dias.length === 1;
  const [agora, setAgora] = useState(() => new Date());
  // Linha do "agora" anda sozinha enquanto a aba fica aberta.
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const porDia = useMemo(() => {
    return dias.map((dia) => {
      const reunioesDoDia = reunioes
        .map((r) => ({ r, rec: recorteNoDia(r.meeting.startAt, r.meeting.endAt, dia) }))
        .filter((x): x is { r: Reuniao; rec: Intervalo } => x.rec != null);

      const bloqueiosDoDia = bloqueios
        .map((b) => ({ b, rec: recorteNoDia(b.block.startAt, b.block.endAt, dia) }))
        .filter((x): x is { b: Bloqueio; rec: Intervalo } => x.rec != null);

      // Dia inteiro = flag `allDay` ou cobre da meia-noite à meia-noite.
      const diaInteiro = bloqueiosDoDia.filter(
        ({ b, rec }) => b.block.allDay || (rec.ini === 0 && rec.fim >= MINUTOS_NO_DIA - 1),
      );
      const restantes = bloqueiosDoDia.filter((x) => !diaInteiro.includes(x));
      // Compromisso do Google com hora entra no layout lado a lado das
      // reuniões — como cartão, não como hachura de fundo: senão "Terapia
      // 15h" some embaixo da reunião das 14h às 16h.
      const doGoogle = restantes.filter(({ b }) => b.block.externalProvider === "google");
      const parciais = restantes.filter(({ b }) => b.block.externalProvider !== "google");

      const disponivel = faixas
        .filter((f) => f.dayOfWeek === dia.getDay())
        .map((f) => ({ ini: hhmmParaMinutos(f.startTime), fim: hhmmParaMinutos(f.endTime) }));

      return { dia, reunioes: reunioesDoDia, diaInteiro, parciais, doGoogle, disponivel };
    });
  }, [dias, reunioes, bloqueios, faixas]);

  // Faixa de horas: comercial, alargada pelo que cair fora dela. Bloqueio de
  // dia inteiro não conta — senão qualquer férias esticava a grade para 24h.
  const horas = useMemo(() => {
    const tudo: Intervalo[] = [];
    for (const d of porDia) {
      for (const x of d.reunioes) tudo.push(x.rec);
      for (const x of d.parciais) tudo.push(x.rec);
      for (const x of d.disponivel) tudo.push(x);
    }
    return faixaDeHoras(tudo);
  }, [porDia]);

  const janela: Intervalo = { ini: horas.ini * 60, fim: horas.fim * 60 };
  const alturaTotal = (horas.fim - horas.ini) * PX_POR_HORA;
  const top = (min: number) => (min - janela.ini) * PX_POR_MINUTO;
  const altura = (rec: Intervalo) =>
    Math.max(18, (Math.min(rec.fim, janela.fim) - Math.max(rec.ini, janela.ini)) * PX_POR_MINUTO);

  // Sem semana típica cadastrada não há o que sombrear — cinza em tudo
  // pareceria bug, não informação.
  const temDisponibilidade = faixas.length > 0;
  const temGoogle = bloqueios.some((b) => b.block.externalProvider === "google");
  const temBloqueio = bloqueios.some((b) => b.block.externalProvider !== "google");

  function marcarNoClique(e: MouseEvent<HTMLDivElement>, dia: Date) {
    if (!podeMarcar) return;
    // Só o fundo da coluna: clique no card já abre a reunião.
    if (e.target !== e.currentTarget) return;
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const minutos = janela.ini + Math.floor(y / PX_POR_MINUTO / 30) * 30;
    const quando = new Date(dia);
    quando.setHours(Math.floor(minutos / 60), minutos % 60, 0, 0);
    // Fora do horário de trabalho (a área cinza) e horário bloqueado não
    // recebem reunião — o servidor recusaria; avisar aqui poupa o formulário.
    // Bloquear continua possível: é justamente o almoço e a noite que a
    // pessoa mais quer bloquear (dentista, compromisso pessoal).
    let motivoSemReuniao: string | undefined;
    if (temDisponibilidade) {
      const doDia = faixas
        .filter((f) => f.dayOfWeek === dia.getDay())
        .map((f) => ({ ini: hhmmParaMinutos(f.startTime), fim: hhmmParaMinutos(f.endTime) }));
      if (!doDia.some((f) => f.ini <= minutos && minutos < f.fim)) motivoSemReuniao = "Fora do horário de trabalho";
    }
    const bloqueado = bloqueios.find(
      (b) =>
        b.block.busy !== false &&
        b.block.externalProvider == null &&
        // No modo equipe o bloqueio pode ser de outro colega — aí a reunião
        // é minha e o servidor decide; o MEU bloqueio recusa em qualquer modo.
        (!modoEquipe || b.block.userId === meuId) &&
        new Date(b.block.startAt) <= quando &&
        new Date(b.block.endAt) > quando,
    );
    if (bloqueado) motivoSemReuniao = `Horário bloqueado${bloqueado.block.reason ? ` (${bloqueado.block.reason})` : ""}`;

    if (onBloquear) {
      setMenu({ x: e.clientX, y: e.clientY, quando, motivoSemReuniao });
      return;
    }
    if (motivoSemReuniao) {
      toast.error(`${motivoSemReuniao}. Escolha outro horário.`);
      return;
    }
    onMarcar(quando);
  }

  // Compromisso trazido do Google Agenda: âmbar em vez de vermelho, para a
  // pessoa distinguir "bloqueei de propósito" de "está no meu Google".
  const ehGoogle = (b: Bloqueio) => b.block.externalProvider === "google";

  function bloqueioClicavel(b: Bloqueio): boolean {
    return !!onAbrirBloqueio && b.block.sourceMeetingId == null && b.block.externalProvider == null;
  }

  const minutoAgora = agora.getHours() * 60 + agora.getMinutes();

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-border overflow-auto max-h-[72vh]">
        <div
          className={`grid ${umDia ? "" : "min-w-[56rem]"}`}
          style={{ gridTemplateColumns: `3.25rem repeat(${dias.length}, minmax(0, 1fr))` }}
        >
          {/* ── Cabeçalho ──────────────────────────────────────── */}
          <div className="sticky top-0 z-20 bg-background border-b border-border" />
          {porDia.map(({ dia, diaInteiro, reunioes: doDia }) => {
            const hoje = ehHoje(dia);
            // Só o que OCUPA fecha a coluna; "Disponível" do Google fica só no chip.
            const bloqueadoODiaTodo = diaInteiro.some(({ b }) => b.block.busy !== false);
            return (
              <div
                key={dia.toISOString()}
                className={`sticky top-0 z-20 border-b border-l border-border px-1.5 py-1.5 min-h-[3.25rem] ${
                  bloqueadoODiaTodo
                    ? "bg-rose-100/70 dark:bg-rose-950/50"
                    : hoje
                      ? "bg-primary/5"
                      : "bg-background"
                }`}
              >
                <div className="flex items-baseline justify-between gap-1">
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {umDia ? DIAS_LONGOS[dia.getDay()] : DIAS_CURTOS[dia.getDay()]}
                  </span>
                  <button
                    type="button"
                    disabled={umDia || !onEscolherDia}
                    onClick={() => onEscolherDia?.(dia)}
                    title={umDia ? undefined : "Ver só este dia"}
                    className={`text-sm font-semibold leading-none rounded-full h-6 w-6 inline-flex items-center justify-center ${
                      hoje ? "bg-primary text-primary-foreground" : ""
                    } ${umDia || !onEscolherDia ? "" : "hover:ring-2 hover:ring-primary/40"}`}
                  >
                    {dia.getDate()}
                  </button>
                </div>
                {diaInteiro.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {diaInteiro.slice(0, umDia ? 8 : 3).map(({ b }) => (
                      <Badge
                        key={b.block.id}
                        role={bloqueioClicavel(b) ? "button" : undefined}
                        onClick={() => bloqueioClicavel(b) && onAbrirBloqueio?.(b)}
                        variant="outline"
                        className={`rounded-md border-0 ${
                          ehGoogle(b) ? (b.block.busy === false ? CARTAO_GOOGLE_LIVRE : CARTAO_GOOGLE) : CARTAO_BLOQUEIO
                        } text-[10px] px-1.5 py-0 h-5 gap-1 max-w-full font-medium ${
                          bloqueioClicavel(b) ? "cursor-pointer hover:brightness-95" : ""
                        }`}
                        title={rotuloDoBloqueio(b, modoEquipe) + (bloqueioClicavel(b) ? " · clique para editar" : "")}
                      >
                        {ehGoogle(b) ? <GoogleCalendarIcon className="h-3 w-3" /> : <CalendarOff className="h-2.5 w-2.5 shrink-0" />}
                        <span className="truncate">
                          {modoEquipe
                            ? `${primeiroNome(b.user)}${b.block.reason ? ` · ${b.block.reason}` : ""}`
                            : b.block.reason || "Bloqueado"}
                        </span>
                      </Badge>
                    ))}
                    {diaInteiro.length > (umDia ? 8 : 3) && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                        +{diaInteiro.length - (umDia ? 8 : 3)}
                      </Badge>
                    )}
                  </div>
                ) : (
                  doDia.length > 0 && (
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {doDia.length} {doDia.length === 1 ? "reunião" : "reuniões"}
                    </p>
                  )
                )}
              </div>
            );
          })}

          {/* ── Coluna das horas ──────────────────────────────── */}
          <div className="relative" style={{ height: alturaTotal }}>
            {Array.from({ length: horas.fim - horas.ini }, (_, i) => horas.ini + i).map((h) => (
              <div
                key={h}
                className="absolute right-1.5 -translate-y-1/2 text-[10px] text-muted-foreground tabular-nums"
                style={{ top: (h - horas.ini) * PX_POR_HORA }}
              >
                {h > horas.ini && `${String(h).padStart(2, "0")}:00`}
              </div>
            ))}
          </div>

          {/* ── Colunas dos dias ──────────────────────────────── */}
          {porDia.map(({ dia, reunioes: doDia, diaInteiro, parciais, doGoogle, disponivel }) => {
            const hoje = ehHoje(dia);
            // Só o que OCUPA fecha a coluna; "Disponível" do Google fica só no chip.
            const bloqueadoODiaTodo = diaInteiro.some(({ b }) => b.block.busy !== false);
            // Reuniões e compromissos do Google dividem a largura entre si.
            const posicoes = empacotar([...doDia.map((x) => x.rec), ...doGoogle.map((x) => x.rec)]);
            const foraDoHorario = temDisponibilidade ? complemento(janela, disponivel) : [];

            return (
              <div
                key={dia.toISOString()}
                className={`relative border-l border-border ${hoje ? "bg-primary/[0.03]" : ""} ${
                  podeMarcar && !bloqueadoODiaTodo ? "cursor-pointer" : ""
                }`}
                style={{ height: alturaTotal }}
                onClick={(e) => !bloqueadoODiaTodo && marcarNoClique(e, dia)}
              >
                {/* Linhas de hora */}
                {Array.from({ length: horas.fim - horas.ini }, (_, i) => (
                  <div
                    key={i}
                    className="absolute inset-x-0 border-t border-border/60 pointer-events-none"
                    style={{ top: i * PX_POR_HORA }}
                  />
                ))}

                {/* Fora do horário de trabalho */}
                {foraDoHorario.map((f, i) => (
                  <div
                    key={`fora-${i}`}
                    className="absolute inset-x-0 bg-muted/60 pointer-events-none"
                    style={{ top: top(f.ini), height: altura(f) }}
                  />
                ))}

                {/* Dia inteiro bloqueado: coluna toda hachurada, sem clique */}
                {bloqueadoODiaTodo && (
                  <div
                    className="absolute inset-0 bg-rose-200/45 dark:bg-rose-950/40 pointer-events-none"
                    aria-label="Dia bloqueado"
                  />
                )}

                {/* Bloqueios parciais */}
                {parciais.map(({ b, rec }) => (
                  <div
                    key={`b-${b.block.id}`}
                    role={bloqueioClicavel(b) ? "button" : undefined}
                    tabIndex={bloqueioClicavel(b) ? 0 : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (bloqueioClicavel(b)) onAbrirBloqueio?.(b);
                    }}
                    onKeyDown={(e) => {
                      if (bloqueioClicavel(b) && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onAbrirBloqueio?.(b);
                      }
                    }}
                    className={`absolute inset-x-0.5 rounded-md px-1.5 py-0.5 overflow-hidden ${
                      ehGoogle(b) ? CARTAO_GOOGLE : CARTAO_BLOQUEIO
                    } ${bloqueioClicavel(b) ? "cursor-pointer hover:brightness-95" : "pointer-events-none"}`}
                    style={{ top: top(rec.ini), height: altura(rec) }}
                    title={rotuloDoBloqueio(b, modoEquipe) + (bloqueioClicavel(b) ? " · clique para editar" : "")}
                  >
                    <p className="text-[10px] font-medium leading-tight truncate flex items-center gap-1">
                      {ehGoogle(b) ? <GoogleCalendarIcon className="h-3 w-3" /> : <CalendarOff className="h-2.5 w-2.5 shrink-0" />}
                      <span className="truncate">
                        {modoEquipe && b.user ? `${primeiroNome(b.user)} · ` : ""}
                        {ehGoogle(b) ? `${hhmm(b.block.startAt)} ` : ""}
                        {b.block.reason || "Bloqueado"}
                      </span>
                    </p>
                  </div>
                ))}

                {/* Reuniões */}
                {doDia.map(({ r, rec }, i) => {
                  const pos = posicoes[i];
                  const h = altura(rec);
                  const compacta = h < 44;
                  // Com uma coluna só há espaço de sobra: mostra contato e
                  // participantes mesmo em reunião curta.
                  const detalhes = umDia || h >= 64;
                  const participantes = umDia || h >= 80;
                  const largura = 100 / pos.total;
                  const status = STATUS_REUNIAO[r.meeting.status];
                  const nomes = (r.attendees ?? [])
                    .map((a) => a.user?.name?.split(" ")[0])
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <button
                      key={r.meeting.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAbrirReuniao(r.meeting.id);
                      }}
                      title={`${hhmm(r.meeting.startAt)} – ${hhmm(r.meeting.endAt)} · ${r.meeting.title}${nomes ? ` · ${nomes}` : ""}`}
                      className={`absolute text-left rounded-md border-l-[3px] bg-card border border-border shadow-sm hover:bg-accent hover:z-10 transition-colors overflow-hidden px-1.5 ${
                        compacta ? "py-0" : "py-1"
                      } ${status?.barra ?? "border-l-slate-400"} ${
                        r.meeting.status === "done" || r.meeting.status === "no_show" ? "opacity-70" : ""
                      }`}
                      style={{
                        top: top(rec.ini),
                        height: h,
                        left: `calc(${pos.coluna * largura}% + 2px)`,
                        width: `calc(${largura}% - 4px)`,
                      }}
                    >
                      {compacta ? (
                        <p className="text-[11px] leading-[18px] truncate">
                          <span className="font-medium text-muted-foreground">{hhmm(r.meeting.startAt)}</span>{" "}
                          <span className="font-medium">{r.meeting.title}</span>
                        </p>
                      ) : (
                        <>
                          <p className="text-[10px] font-medium text-muted-foreground leading-tight">
                            {hhmm(r.meeting.startAt)} – {hhmm(r.meeting.endAt)}
                          </p>
                          <p className="text-xs font-medium leading-tight line-clamp-2">{r.meeting.title}</p>
                          {r.contact?.name && detalhes && (
                            <p className="text-[10px] text-muted-foreground truncate">{r.contact.name}</p>
                          )}
                          {modoEquipe && nomes && participantes && (
                            <p className="text-[10px] text-muted-foreground truncate">{nomes}</p>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}

                {/* Compromissos do Google com hora, lado a lado com as reuniões */}
                {doGoogle.map(({ b, rec }, j) => {
                  const pos = posicoes[doDia.length + j];
                  const h = altura(rec);
                  const compacta = h < 44;
                  const largura = 100 / pos.total;
                  const livre = b.block.busy === false;
                  return (
                    <div
                      key={`g-${b.block.id}`}
                      title={rotuloDoBloqueio(b, modoEquipe) + (livre ? " · marcado como Disponível no Google" : "")}
                      className={`absolute rounded-md overflow-hidden px-1.5 ${compacta ? "py-0" : "py-1"} ${
                        livre ? CARTAO_GOOGLE_LIVRE : CARTAO_GOOGLE
                      }`}
                      style={{
                        top: top(rec.ini),
                        height: h,
                        left: `calc(${pos.coluna * largura}% + 2px)`,
                        width: `calc(${largura}% - 4px)`,
                      }}
                    >
                      {compacta ? (
                        <p className="text-[11px] leading-[18px] truncate flex items-center gap-1">
                          <GoogleCalendarIcon className="h-3 w-3" />
                          <span className="font-medium opacity-70">{hhmm(b.block.startAt)}</span>
                          <span className="font-medium truncate">{b.block.reason || "Compromisso"}</span>
                        </p>
                      ) : (
                        <>
                          <p className="text-[10px] font-medium leading-tight flex items-center gap-1 opacity-70">
                            <GoogleCalendarIcon className="h-3 w-3" />
                            {hhmm(b.block.startAt)} – {hhmm(b.block.endAt)}
                          </p>
                          <p className="text-xs font-medium leading-tight line-clamp-2">
                            {modoEquipe && b.user ? `${primeiroNome(b.user)} · ` : ""}
                            {b.block.reason || "Compromisso"}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}

                {/* Agora */}
                {hoje && minutoAgora >= janela.ini && minutoAgora <= janela.fim && (
                  <div
                    className="absolute inset-x-0 z-10 pointer-events-none"
                    style={{ top: top(minutoAgora) }}
                  >
                    <div className="h-0.5 bg-red-500" />
                    <div className="absolute -left-1 -top-[3px] h-2 w-2 rounded-full bg-red-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {menu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenu(null)} aria-hidden />
          <div
            role="menu"
            className="fixed z-50 w-56 rounded-md border border-border bg-popover text-popover-foreground shadow-md p-1"
            style={{
              left: Math.min(menu.x, window.innerWidth - 240),
              top: Math.min(menu.y, window.innerHeight - 110),
            }}
          >
            <p className="px-2 py-1 text-[11px] text-muted-foreground">
              {menu.quando.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" })} · {hhmm(menu.quando)}
            </p>
            {menu.motivoSemReuniao ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-2">
                <CalendarPlus className="h-4 w-4 shrink-0 opacity-50" />
                <span>{menu.motivoSemReuniao} — sem reunião aqui.</span>
              </p>
            ) : (
              <button
                type="button"
                role="menuitem"
                className="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                onClick={() => {
                  onMarcar(menu.quando);
                  setMenu(null);
                }}
              >
                <CalendarPlus className="h-4 w-4 text-muted-foreground" />
                Marcar reunião
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => {
                onBloquear?.(menu.quando, new Date(menu.quando.getTime() + 3600_000));
                setMenu(null);
              }}
            >
              <CalendarOff className="h-4 w-4 text-rose-700" />
              Bloquear este horário
            </button>
          </div>
        </>
      )}

      {/* ── Legenda ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground px-1">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm border border-border border-l-[3px] border-l-sky-500 bg-card" />
          Reunião
        </span>
        {temBloqueio && (
          <span className="inline-flex items-center gap-1.5">
            <span className={`h-3 w-3 rounded-sm ${CARTAO_BLOQUEIO}`} />
            Agenda bloqueada
          </span>
        )}
        {temGoogle && (
          <span className="inline-flex items-center gap-1.5">
            <GoogleCalendarIcon className="h-3.5 w-3.5" />
            Google Agenda
          </span>
        )}
        {temDisponibilidade && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm bg-muted/80 border border-border" />
            Fora do horário de trabalho
          </span>
        )}
        {podeMarcar && (
          <span className="ml-auto hidden sm:inline">
            {onBloquear ? "Clique num horário vazio para marcar ou bloquear" : "Clique num horário vazio para marcar"}
          </span>
        )}
      </div>
    </div>
  );
}

function primeiroNome(u?: { name?: string | null; email?: string | null } | null): string {
  return u?.name?.split(" ")[0] ?? u?.email ?? "—";
}

function rotuloDoBloqueio(b: Bloqueio, modoEquipe: boolean): string {
  const quem = modoEquipe && b.user ? `${b.user.name ?? b.user.email ?? ""}: ` : "";
  const motivo = b.block.reason || "Agenda bloqueada";
  const origem =
    b.block.sourceMeetingId != null
      ? " (reunião em outra empresa)"
      : b.block.externalProvider === "google"
        ? " (Google Agenda)"
        : "";
  return `${quem}${motivo}${origem} · ${hhmm(b.block.startAt)} – ${hhmm(b.block.endAt)}`;
}
